import { saveAs } from 'file-saver';
import { formatCentsToUsd } from '../../../helpers/formatCentsToUsd';
import { centsToXlsxNumber, applyCurrencyFormat } from '../../../helpers/excelExportHelpers';
import { TIMEZONE } from '../../../../config';

/*
 * COLUMN ORDER IS FROZEN. BASE_HEADERS below mirrors the FareHarbor affiliate
 * invoice export column-for-column, and the client's invoicing workflow reads it
 * by column position. Do not reorder these columns to match the on-screen table
 * or the other reports - the affiliate invoice table on screen follows the
 * standardized column order, this export deliberately does not. Any change here
 * needs client sign-off first.
 */

const FILE_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

const dateParts = (iso) => {
    if (!iso) {
        return { date: '', time: '' };
    }
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) {
        return { date: '', time: '' };
    }
    return {
        date: date.toLocaleDateString(undefined, { timeZone: TIMEZONE }),
        time: date.toLocaleTimeString([], {
            hour: 'numeric',
            minute: '2-digit',
            timeZone: TIMEZONE,
        }),
    };
};

const BASE_HEADERS = [
    'Booking ID',
    'Booking Status',
    'Created At',
    'Created At Date',
    'Created At Time',
    'Last Modified At',
    'Last Modified At Date',
    'Last Modified At Time',
    'Show',
    'Item',
    'Availability',
    'Start Date',
    'Start Time',
    'Name',
    'Phone',
    'Contact Language',
    'Email',
    'Booking Notes',
    'Booking Cancellation Notes',
    'Billing ZIP codes',
    'Pax',
    '# of Pax',
    'Online Booking Reference',
    'Subtotal',
    'Tax',
    'Booking Total',
    'Revenue',
    'Total Paid',
    'Processing Fees',
    'Total Paid after Processing Fees',
    'Amount Due',
    'Paid Status',
    'Affiliate',
    'Voucher',
    'Agent',
    'Affiliate %',
    'Invoice Amount',
    'Receivable from Affiliate',
    'Payable to Affiliate',
];

const BASE_HEADER_SET = new Set(BASE_HEADERS);

const money = (value) => formatCentsToUsd(Number(value || 0));

const collectDynamicHeaders = (items) => {
    const ticketHeaders = new Set();
    const customFieldHeaders = new Set();

    items.forEach((item) => {
        (item.ticketBreakdown || []).forEach((ticket) => {
            const idPart = ticket.id ? ` (#${ticket.id})` : '';
            ticketHeaders.add(`${ticket.name}${idPart}`);
        });

        Object.keys(item.customFieldMap || {}).forEach((label) => {
            if (!label) return;
            if (!BASE_HEADER_SET.has(label)) {
                customFieldHeaders.add(label);
            }
        });
    });

    return {
        ticketHeaders: Array.from(ticketHeaders).sort((a, b) => a.localeCompare(b)),
        customFieldHeaders: Array.from(customFieldHeaders).sort((a, b) => a.localeCompare(b)),
    };
};

const flattenItems = (reportData) =>
    (reportData.groups || []).flatMap((group) =>
        (group.items || []).map((item) => ({
            ...item,
            __groupName: group.commissionGroupName,
            __showTitle: group.showTitle,
        })),
    );

const mapBaseRow = (item) => {
    const created = dateParts(item.bookingCreatedAtIso);
    const updated = dateParts(item.bookingUpdatedAtIso);
    const start = dateParts(item.availabilityStartIso);

    const itemLabel = (item.ticketBreakdown || [])
        .map((ticket) => ticket.name)
        .filter(Boolean)
        .join(', ');

    return {
        'Booking ID': item.bookingId,
        'Booking Status': item.status || '',
        'Created At': item.bookingDate || '',
        'Created At Date': created.date,
        'Created At Time': created.time,
        'Last Modified At': updated.date ? `${updated.date} ${updated.time}`.trim() : '',
        'Last Modified At Date': updated.date,
        'Last Modified At Time': updated.time,
        Show: item.showTitle || item.__showTitle || '',
        Item: itemLabel,
        Availability: item.availability || '',
        'Start Date': start.date,
        'Start Time': start.time,
        Name: item.contact || '',
        Phone: item.phone || '',
        'Contact Language': item.language || '',
        Email: item.email || '',
        'Booking Notes': item.bookingNotes || '',
        'Booking Cancellation Notes': item.bookingCancellationNotes || '',
        'Billing ZIP codes': item.billingZipCodes || '',
        Pax: item.customers || '',
        '# of Pax': item.customerCount || 0,
        'Online Booking Reference': item.onlineBookingReference || '',
        Subtotal: centsToXlsxNumber(item.subtotalAmount),
        Tax: centsToXlsxNumber(item.taxAmount),
        'Booking Total': centsToXlsxNumber(item.grossAmount),
        Revenue: centsToXlsxNumber(item.netAmount),
        'Total Paid': centsToXlsxNumber(item.totalPaidAmount),
        'Processing Fees': centsToXlsxNumber(item.processingFees),
        'Total Paid after Processing Fees': centsToXlsxNumber(item.totalPaidAfterProcessingFees),
        'Amount Due': centsToXlsxNumber(item.amountDue),
        'Paid Status': item.paidStatus || '',
        Affiliate: item.commissionGroupName || item.__groupName || '',
        Voucher: item.confirmationCode || '',
        Agent: item.agentName || '',
        'Affiliate %': item.affiliatePercent || '',
        'Invoice Amount': centsToXlsxNumber(item.invoiceTotal),
        'Receivable from Affiliate': centsToXlsxNumber(item.receivableFromAffiliate),
        'Payable to Affiliate': centsToXlsxNumber(item.payableToAffiliate),
    };
};

export const exportAffiliateInvoiceReportToXlsx = async (reportData, dateFrom, dateTo) => {
    const XLSX = await import('xlsx');
    const items = flattenItems(reportData);
    const { ticketHeaders, customFieldHeaders } = collectDynamicHeaders(items);
    const uniqueShowDates = new Set(
        items
            .map((item) => {
                if (!item.availabilityStartIso) return '';
                const date = new Date(item.availabilityStartIso);
                if (Number.isNaN(date.getTime())) return '';
                return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            })
            .filter(Boolean),
    );
    const hideStartDateColumn = uniqueShowDates.size === 1;

    const headers = [...BASE_HEADERS, ...ticketHeaders, ...customFieldHeaders].filter(
        (header) => !(hideStartDateColumn && header === 'Start Date'),
    );

    const rows = items.map((item) => {
        const row = mapBaseRow(item);

        ticketHeaders.forEach((header) => {
            row[header] = '';
        });

        (item.ticketBreakdown || []).forEach((ticket) => {
            const idPart = ticket.id ? ` (#${ticket.id})` : '';
            const header = `${ticket.name}${idPart}`;
            if (header in row) {
                row[header] = ticket.count;
            }
        });

        customFieldHeaders.forEach((header) => {
            row[header] = item.customFieldMap?.[header] || '';
        });

        return headers.map((header) => row[header] ?? '');
    });

    const sheetRows = [
        ['Affiliate Invoice Report'],
        [
            `Date Range: ${new Date(dateFrom + 'T12:00:00').toLocaleDateString()} - ${new Date(dateTo + 'T12:00:00').toLocaleDateString()}`,
        ],
        [
            `Total Gross: ${money(reportData.totalGrossAmount || 0)}`,
            `Total Commission: ${money(reportData.totalCommissionAmount || 0)}`,
            `Total Net: ${money(reportData.totalNetAmount || 0)}`,
        ],
        [],
        headers,
        ...rows,
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetRows);
    ws['!cols'] = headers.map((header) => {
        const width = Math.max(12, Math.min(42, String(header).length + 2));
        return { wch: width };
    });

    // Apply currency format to all relevant columns by header name
    const currencyHeaders = [
        'Subtotal',
        'Tax',
        'Booking Total',
        'Revenue',
        'Total Paid',
        'Processing Fees',
        'Total Paid after Processing Fees',
        'Amount Due',
        'Invoice Amount',
        'Receivable from Affiliate',
        'Payable to Affiliate',
    ];
    const currencyColIndices = currencyHeaders
        .map((h) => headers.indexOf(h))
        .filter((idx) => idx !== -1);
    applyCurrencyFormat(XLSX, ws, sheetRows, currencyColIndices);

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Affiliate Invoice');

    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const file = new Blob([excelBuffer], { type: FILE_TYPE });
    saveAs(file, `affiliate-invoice-report-${dateFrom}-to-${dateTo}.xlsx`);
};
